import { deepClone } from '@alova/shared'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  getWxCode,
  requestGetUserClubList,
  requestGetUserInfo,
  requestPostLoginToken,
  requestPostRefreshShortToken,
} from '@/api/login/login'
import {
  BackUserInfo,
  IBackTokenInfo,
  UserInfoRole,
  UserJoinedClubInfo,
  WxPlatformLoginData,
} from '@/api/login/loginType'
import { useMemberModulesStore } from '@/store/memberModules'
import { usePermissionStore } from '@/store/permission'
import { usePlayerOnlineStatusStore } from '@/store/playerOnlineStatus'
import { EnumIdType } from '@/typings'

export interface StoreUserInfo extends Omit<BackUserInfo, 'currentRole'> {
  currentRole: StoreUserInfoRole
}

export interface StoreUserInfoRole extends Omit<UserInfoRole, 'roleCode'> {
  roleCode: EnumIdType | ''
}

// 初始化状态
const userInfoState: StoreUserInfo = {
  id: '',
  clubId: '',
  userId: '',
  memberNo: '',
  nickName: '',
  customTitle: '',
  avatarUrl: '',
  status: 0,
  totalTurnover: '',
  level: 0,
  joinedBy: '',
  createdAt: '',
  updatedAt: '',
  /** 顾客游戏ID */
  gameUid: '',
  /** 微信号 */
  wechatId: '',
  /** 手机号 */
  phone: '',
  currentRole: { roleCode: '', roleName: '' },
  roles: [],
  club: {
    id: '',
    name: '',
    avatarUrl: '',
    backgroundUrl: '',
    description: '',
    status: 0,
    leagueName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    rootLeagueName: '',
  },
  clubMemberInfo: {
    birthday: '',
  },
}

type LoggedClubInfo = {
  clubId: string
  role: EnumIdType
} | {
  clubId: string
  role: ''
}

// 上次登录的俱乐部和身份
const loggedClubState: LoggedClubInfo = {
  clubId: '',
  role: '',
}

// token信息
export interface IBackTokenInfoState extends IBackTokenInfo {
  refresh_token_expires_in: number
}

// 存放token相关信息
const tokenInfoState: IBackTokenInfoState = {
  access_token: '',
  expires_in: 0,
  refresh_token: '',
  token_type: '',
  refresh_token_expires_in: 0,
}

// 长token有效期 30天
const refreshTokenExpires = 29 * 24 * 60 * 60 * 1000

export const useUserStore = defineStore(
  'user',
  () => {
    // 定义用户信息
    const userInfo = ref<StoreUserInfo>(deepClone(userInfoState))

    // 定义token信息
    const tokenInfo = ref<IBackTokenInfoState>({ ...tokenInfoState })

    // 上次登录的俱乐部和身份
    const loggedClubInfo = ref<LoggedClubInfo>({ ...loggedClubState })

    /**
     * 判断用户是否已登录
     * 检查条件：
     * 1. refresh_token 存在且非空
     * 2. refresh_token 未过期
     * 3. 用户信息有效（有角色信息）
     * @returns {boolean} 是否已登录
     */
    function isLogin() {
      const now = Date.now()
      const hasValidRefreshToken
        = tokenInfo.value.refresh_token
          && tokenInfo.value.refresh_token !== ''
          && tokenInfo.value.refresh_token_expires_in > now

      const hasValidUserInfo
        = userInfo.value.userId && userInfo.value.roles.length > 0

      return hasValidRefreshToken && hasValidUserInfo
    }

    // 设置用户信息
    function setUserInfo(val: BackUserInfo | StoreUserInfo) {
      userInfo.value = val
    }

    // 设置token
    function setTokenInfo(BackTokenInfo: IBackTokenInfo) {
      tokenInfo.value.access_token = BackTokenInfo.access_token
      tokenInfo.value.refresh_token = BackTokenInfo.refresh_token
      tokenInfo.value.token_type = BackTokenInfo.token_type
      tokenInfo.value.expires_in = BackTokenInfo.expires_in * 1000 + new Date().getTime()
      tokenInfo.value.refresh_token_expires_in = new Date().getTime() + refreshTokenExpires
    }

    // 设置登录记录
    function setLoggedClubInfo(data: LoggedClubInfo) {
      loggedClubInfo.value.clubId = data.clubId
      loggedClubInfo.value.role = data.role
    }

    // 删除token
    function removeTokenInfo() {
      tokenInfo.value = { ...tokenInfoState }
      uni.removeStorageSync('token')
    }

    // 删除用户信息
    function removeUserInfo() {
      userInfo.value = deepClone(userInfoState)
      uni.removeStorageSync('userInfo')
    }

    // 删除登录的俱乐部信息
    function removeLoggedClubInfo() {
      loggedClubInfo.value = { ...loggedClubState }
    }

    /**
     * 判断是否存在某个身份
     * @param roleCode,身份码
     */
    function hasRoleCode(roleCode: EnumIdType) {
      return userInfo.value.roles.some(item => item.roleCode === roleCode)
    }

    /**
     * 退出登录 并 删除用户信息
     */
    function logout() {
      removeUserInfo()
      removeTokenInfo()
      resetMemberModules() // 退出登录时重置会员端模块开关
      resetPlayerOnlineStatus()
    }

    // 获取用户信息
    async function getUserInfo() {
      const { data } = await requestGetUserInfo()
      // 用户信息获取成功,
      setUserInfo(data)
      setLoggedClubInfo({ role: data.currentRole.roleCode, clubId: data.clubId })
      await Promise.all([
        initPermissionMenu(),
        initMemberModules(data.clubId),
      ])
    }

    /**
     * 初始化会员端模块开关
     */
    async function initMemberModules(clubId: string) {
      const memberModulesStore = useMemberModulesStore()

      try {
        await memberModulesStore.fetchEnabledModules(clubId)
      }
      catch (error) {
        console.error('初始化会员端模块开关失败:', error)
      }
    }

    /**
     * 重置会员端模块开关
     */
    function resetMemberModules() {
      const memberModulesStore = useMemberModulesStore()
      memberModulesStore.resetEnabledModules()
    }

    /**
     * 重置陪玩在线状态
     */
    function resetPlayerOnlineStatus() {
      const playerOnlineStatusStore = usePlayerOnlineStatusStore()
      playerOnlineStatusStore.resetStatus()
    }

    /**
     * 初始化权限菜单
     */
    async function initPermissionMenu() {
      // 权限菜单
      const permissionStore = usePermissionStore()

      try {
        await permissionStore.fetchPermissionMenu()
      }
      catch (error) {
        console.error('初始化权限菜单失败:', error)
      }
    }

    /**
     * 获取用户所有的俱乐部信息
     * @return 即将要登录的身份信息,0表示未注册
     */
    async function getAllUserClubInfo() {
      const { data } = await requestGetUserClubList()

      // 返回的当前登录俱乐部、身份信息
      const defaultClubInfo: UserJoinedClubInfo | undefined = data.find((item) => {
        return item.roles.length > 0
      })

      // 是否使用登录记录
      let useLoginHistory = false

      // 不存在登录记录缓存，查找合适的登录身份。地没有用户登录的俱乐部和身份记录时，从俱乐部列表中取出第一个拥有身份的俱乐部作为默认登录的俱乐部，登录身份同理

      if (loggedClubInfo.value.clubId) {
        // 存在登录记录缓存，判断身份、俱乐部信息是否有变动
        const oldLoginClubInfo = data.find(item => item.id === loggedClubInfo.value.clubId)

        if (oldLoginClubInfo) {
          if (oldLoginClubInfo.roles.some(item => item.roleCode === loggedClubInfo.value.role)) {
            // 用户的历史登录信息依旧存在，使用历史登录信息
            useLoginHistory = true
          }
        }
      }

      if (!useLoginHistory && defaultClubInfo) {
        // 不使用登录记录，但是存在可以使用登录身份
        loggedClubInfo.value.clubId = defaultClubInfo.id
        loggedClubInfo.value.role = defaultClubInfo.roles[0].roleCode
        return {
          clubId: loggedClubInfo.value.clubId,
          role: loggedClubInfo.value.role,
        }
      }
      else if (useLoginHistory) {
        // 存在登录记录，并使用登录记录
        return {
          clubId: loggedClubInfo.value.clubId,
          role: loggedClubInfo.value.role,
        }
      }
      else {
        return 0
      }
    }

    /**
     * 微信登录
     * @param loingInfo 拓展参数，
     * @param loingInfo.key 账号转移参数
     * @return 平台token
     */
    async function wxLogin(loingInfo?: { transferKey?: string }) {
      // 获取微信小程序登录的code
      const data = await getWxCode()

      const wxLoginData: WxPlatformLoginData = {
        code: data.code,
      }

      if (loingInfo && loingInfo.transferKey) {
        wxLoginData.transferKey = loingInfo.transferKey
      }

      const res: any = await requestPostLoginToken(wxLoginData)

      const backTokenInfo = res as IBackTokenInfo

      setTokenInfo(backTokenInfo)

      return res
    }

    /**
     * 刷新短token
     * @param loginedInfo,要切换的目标俱乐部和身份
     */
    async function refreshShortToken(targetClub?: { clubId?: string, role?: EnumIdType }) {
      const res: IBackTokenInfo = await requestPostRefreshShortToken({
        refresh_token: tokenInfo.value.refresh_token,
        clubId: targetClub?.clubId ?? loggedClubInfo.value.clubId,
        role: targetClub?.role ?? (loggedClubInfo.value.role as EnumIdType),
      })

      const backTokenInfo = res as IBackTokenInfo

      setTokenInfo(backTokenInfo)

      return res
    }

    /**
     * 切换身份
     */
    async function changeIdType(role: UserInfoRole) {
      await refreshShortToken({ role: role.roleCode })
      await getUserInfo()
      await usePlayerOnlineStatusStore().fetchStatusIfNeeded()
    }

    /**
     * 切换俱乐部
     */
    async function changeClub(clubId: string, role: EnumIdType) {
      await refreshShortToken({ clubId, role })
      await getUserInfo()
      await usePlayerOnlineStatusStore().fetchStatusIfNeeded()
    }

    return {
      tokenInfo,
      userInfo,
      loggedClubInfo,
      wxLogin,
      getUserInfo,
      refreshShortToken,
      getAllUserClubInfo,
      setTokenInfo,
      logout,
      changeIdType,
      hasRoleCode,
      changeClub,
      removeUserInfo,
      isLogin,
      removeTokenInfo,
    }
  },
  {
    persist: true,
  },
)
